// src/webideal-recruit/pages/Settings.jsx
import React, { useState, useEffect } from "react";
import { backendApi } from "../../api/backendApi";
import { 
  User, 
  Lock, 
  Bell, 
  Shield, 
  Save, 
  UserCircle,
  Mail,
  Phone,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";

export default function Settings() {
  const [activeTab, setActiveTab] = useState("profile");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Profile Form
  const [profileForm, setProfileForm] = useState({
    full_name: "",
    email: "",
    phone: ""
  });

  // Password Form
  const [passwordForm, setPasswordForm] = useState({
    current: "",
    new: "",
    confirm: ""
  });

  // Preferences
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    smsNotifications: false,
    theme: "light"
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const currentUser = await backendApi.auth.me();
        setUser(currentUser);
        setProfileForm({
          full_name: currentUser?.full_name || "",
          email: currentUser?.email || "",
          phone: currentUser?.mobile || "" 
        });
      } catch (err) {
        console.error("Failed to load user:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await backendApi.auth.updateProfile({
        phone: profileForm.phone,
        email: profileForm.email
      });
      if (profileForm.full_name !== user?.full_name) {
         await backendApi.entities.UserProfile.updateHeaderInfo({ full_name: profileForm.full_name });
      }
      setMessage({ type: "success", text: "Profile updated successfully!" });
    } catch (err) {
      setMessage({ type: "error", text: "Failed to update profile." });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (passwordForm.new !== passwordForm.confirm) {
      setMessage({ type: "error", text: "New passwords do not match." });
      return;
    }
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      await backendApi.auth.updatePassword(passwordForm.current, passwordForm.new);
      setMessage({ type: "success", text: "Password changed successfully!" });
      setPasswordForm({ current: "", new: "", confirm: "" });
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to change password." });
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: "profile", name: "Account Profile", icon: UserCircle },
    { id: "security", name: "Pass & Security", icon: Shield },
    { id: "notifications", name: "Notifications", icon: Bell },
  ];

  const InputField = ({ label, icon: Icon, type = "text", value, onChange, placeholder, required = false }) => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
      />
    </div>
  );

  if (loading) return <div className="p-8 text-center text-slate-500">Loading settings...</div>;

  return (
    <div className="space-y-8 font-sans">
      <div className="mb-0">
        <h1 className="text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account preferences and security settings.</p>
      </div>

      {message.text && (
        <div className={`p-4 rounded-xl flex items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-300 ${
          message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'
        }`}>
          {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <p className="text-sm font-medium">{message.text}</p>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="w-full lg:w-64 space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setMessage({ type: "", text: "" });
                }}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
                  ${activeTab === tab.id 
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-100 ring-4 ring-blue-50' 
                    : 'text-slate-600 hover:bg-white hover:text-blue-600 hover:shadow-sm'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                {tab.name}
              </button>
            );
          })}
        </div>

        <div className="flex-1">
          {activeTab === "profile" && (
            <Card className="border-none shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Update your personal details and contact information.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileSave} className="space-y-6">
                  <div className="flex items-center gap-6 mb-6">
                    <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-2xl border-4 border-white shadow-md">
                      {user?.full_name ? user.full_name.charAt(0).toUpperCase() : "U"}
                    </div>
                    <div className="flex-1">
                      <Button variant="outline" type="button" className="text-xs h-8 rounded-lg border-slate-200 hover:bg-slate-50">Change Photo</Button>
                      <p className="text-[10px] text-slate-400 mt-2">JPG, GIF or PNG. Max size 2MB</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField
                      label="Full Name"
                      icon={User}
                      value={profileForm.full_name}
                      onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                      placeholder="e.g. John Doe"
                      required
                    />
                    <InputField
                      label="Email Address"
                      icon={Mail}
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                    />
                    <InputField
                      label="Phone Number"
                      icon={Phone}
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl h-11 px-8">
                      {saving ? "Saving..." : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {activeTab === "security" && (
            <Card className="border-none shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle>Password & Security</CardTitle>
                <CardDescription>Update your password to keep your account secure.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordSave} className="space-y-6 max-w-md">
                  <InputField
                    label="Current Password"
                    icon={Lock}
                    type="password"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                    required
                  />
                  <InputField
                    label="New Password"
                    icon={Lock}
                    type="password"
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                    required
                  />
                  <InputField
                    label="Confirm New Password"
                    icon={Lock}
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                    required
                  />
                  <div className="pt-4 border-t border-slate-100">
                    <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl w-full h-11">
                      {saving ? "Updating..." : "Update Password"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {activeTab === "notifications" && (
            <Card className="border-none shadow-sm rounded-2xl">
              <CardHeader>
                <CardTitle>Communication Preferences</CardTitle>
                <CardDescription>Control how we communicate with you.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-5 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-slate-800">Email Notifications</p>
                      <p className="text-xs text-slate-500">Receive emails about new applications and messages.</p>
                    </div>
                    <div 
                      onClick={() => setPreferences({ ...preferences, emailNotifications: !preferences.emailNotifications })}
                      className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors ${preferences.emailNotifications ? 'bg-blue-600' : 'bg-slate-300'}`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${preferences.emailNotifications ? 'translate-x-6' : 'translate-x-0'}`} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-5 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-slate-800">SMS Notifications</p>
                      <p className="text-xs text-slate-500">Get text alerts for urgent interview updates.</p>
                    </div>
                    <div 
                      onClick={() => setPreferences({ ...preferences, smsNotifications: !preferences.smsNotifications })}
                      className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors ${preferences.smsNotifications ? 'bg-blue-600' : 'bg-slate-300'}`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${preferences.smsNotifications ? 'translate-x-6' : 'translate-x-0'}`} />
                    </div>
                  </div>

                  <div className="pt-4 flex justify-end">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl h-11 px-8" onClick={() => setMessage({ type: "success", text: "Preferences saved!" })}>
                      Save Preferences
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}